import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useWorkspaceTabs, suppressAutoTab } from '../../store/workspaceTabsStore';
import { useNavigate, useLocation } from 'react-router';
import { X, Plus, FileText, Search, File, Folder } from 'lucide-react';

const TAB_CLOSE_MOUSE_V_SLOP = 40;
const TAB_CLOSE_MOUSE_H_SLOP = 60;
const NEW_TAB_BUTTON_SLOT = 40; // w-9 (36px) + gap-1 (4px)

function nextActiveTabAfterClose(tabs: { id: string }[], closedIndex: number) {
  if (tabs.length === 0) return null;
  return tabs[closedIndex]?.id ?? tabs[Math.max(0, closedIndex - 1)]?.id ?? null;
}

export default function WorkspaceTabsBar() {
  const tabs = useWorkspaceTabs(s => s.tabs);
  const activeTabId = useWorkspaceTabs(s => s.activeTabId);
  const removeTab = useWorkspaceTabs(s => s.removeTab);
  const addTab = useWorkspaceTabs(s => s.addTab);
  const setActiveTab = useWorkspaceTabs(s => s.setActiveTab);
  const navigate = useNavigate();
  const location = useLocation();
  const tabsBarRef = useRef<HTMLDivElement>(null);
  const tabsScrollRef = useRef<HTMLDivElement>(null);
  const tabsRowRef = useRef<HTMLDivElement>(null);
  const [pinNewTabButton, setPinNewTabButton] = useState(false);
  const closingModeRef = useRef(false);
  const exitClosingModeRef = useRef<(() => void) | null>(null);

  const isTabbableRoute = location.pathname.startsWith('/cases/') || location.pathname.startsWith('/drafts/') || location.pathname === '/workspace/new';

  const exitClosingMode = useCallback(() => {
    if (!closingModeRef.current) return;
    closingModeRef.current = false;
    tabsBarRef.current?.removeAttribute('data-closing-mode');
    exitClosingModeRef.current?.();
    exitClosingModeRef.current = null;
  }, []);

  const nudgeHoverAtPoint = useCallback((clientX: number, clientY: number) => {
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(clientX, clientY);
      if (!target) return;
      const hoverEvent = { bubbles: true, clientX, clientY, view: window } as MouseEventInit;
      target.dispatchEvent(new MouseEvent('mousemove', hoverEvent));
      target.dispatchEvent(new MouseEvent('mouseover', hoverEvent));
    });
  }, []);

  const enterClosingMode = useCallback((clientX: number, clientY: number) => {
    if (closingModeRef.current) {
      nudgeHoverAtPoint(clientX, clientY);
      return;
    }

    closingModeRef.current = true;
    tabsBarRef.current?.setAttribute('data-closing-mode', 'true');

    const onMouseMove = (event: MouseEvent) => {
      const bar = tabsBarRef.current;
      if (!bar) {
        exitClosingMode();
        return;
      }

      const rect = bar.getBoundingClientRect();
      const outsideVertical =
        event.clientY < rect.top - TAB_CLOSE_MOUSE_V_SLOP ||
        event.clientY > rect.bottom + TAB_CLOSE_MOUSE_V_SLOP;
      const outsideHorizontal =
        event.clientX < rect.left - TAB_CLOSE_MOUSE_H_SLOP ||
        event.clientX > rect.right + TAB_CLOSE_MOUSE_H_SLOP;

      if (outsideVertical || outsideHorizontal) {
        exitClosingMode();
      }
    };

    document.addEventListener('mousemove', onMouseMove);
    exitClosingModeRef.current = () => {
      document.removeEventListener('mousemove', onMouseMove);
    };

    nudgeHoverAtPoint(clientX, clientY);
  }, [exitClosingMode, nudgeHoverAtPoint]);

  useEffect(() => () => exitClosingMode(), [exitClosingMode]);

  const updatePinState = useCallback(() => {
    const bar = tabsBarRef.current;
    const row = tabsRowRef.current;
    if (!bar || !row) return;

    const tabItems = row.querySelectorAll('[data-tab-item]');
    let tabsWidth = 0;
    tabItems.forEach((tab, index) => {
      tabsWidth += (tab as HTMLElement).offsetWidth;
      if (index > 0) tabsWidth += 4;
    });

    const styles = getComputedStyle(bar);
    const paddingX = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
    const available = bar.clientWidth - paddingX;

    setPinNewTabButton(tabsWidth + NEW_TAB_BUTTON_SLOT > available);
  }, []);

  useEffect(() => {
    updatePinState();

    const bar = tabsBarRef.current;
    const row = tabsRowRef.current;
    if (!bar || !row) return;

    const observer = new ResizeObserver(() => updatePinState());
    observer.observe(bar);
    observer.observe(row);

    return () => observer.disconnect();
  }, [tabs.length, updatePinState]);

  useEffect(() => {
    requestAnimationFrame(() => updatePinState());
  }, [pinNewTabButton, updatePinState]);

  useEffect(() => {
    const barEl = tabsBarRef.current;
    const scrollEl = tabsScrollRef.current;
    if (!barEl || !scrollEl) return;

    const onWheel = (event: WheelEvent) => {
      // Block page scroll while the pointer is over the tab bar.
      event.preventDefault();
      event.stopPropagation();

      if (scrollEl.scrollWidth <= scrollEl.clientWidth) return;

      const delta =
        Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
      if (delta === 0) return;

      const maxScroll = scrollEl.scrollWidth - scrollEl.clientWidth;
      scrollEl.scrollLeft = Math.min(maxScroll, Math.max(0, scrollEl.scrollLeft + delta));
    };

    barEl.addEventListener('wheel', onWheel, { passive: false, capture: true });
    return () => barEl.removeEventListener('wheel', onWheel, { capture: true });
  }, []);

  const handleTabClick = (tabId: string) => {
    if (location.pathname !== tabId) {
      navigate(tabId);
    }
    setActiveTab(tabId);
  };

  const handleClose = (e: React.MouseEvent, tabId: string) => {
    e.stopPropagation();
    e.preventDefault();
    suppressAutoTab(tabId);

    const { clientX, clientY } = e;
    enterClosingMode(clientX, clientY);

    const state = useWorkspaceTabs.getState();
    const tabIndex = state.tabs.findIndex((t) => t.id === tabId);
    const isClosingActive = state.activeTabId === tabId;
    let nextPath: string | null = null;

    if (isClosingActive) {
      const remaining = state.tabs.filter((t) => t.id !== tabId);
      nextPath = nextActiveTabAfterClose(remaining, tabIndex) ?? '/dashboard';
    }

    if (nextPath) {
      navigate(nextPath, { replace: true });
    }

    removeTab(tabId);
    nudgeHoverAtPoint(clientX, clientY);
  };

  const handleNewTab = () => {
    addTab({
      id: '/workspace/new',
      path: '/workspace/new',
      title: 'New Tab',
      type: 'new'
    });
    navigate('/workspace/new');
  };

  const newTabButton = (
    <button
      type="button"
      onClick={handleNewTab}
      className="case-file-tab workspace-tab-add workspace-tab-inactive flex h-[34px] w-9 shrink-0 items-center justify-center rounded-lg transition-colors"
      aria-label="New tab"
    >
      <Plus className="h-4 w-4" />
    </button>
  );

  return (
    <div className="app-shell-inset relative z-[45] mb-1 mt-2 shrink-0">
      <div
        ref={tabsBarRef}
        className="workspace-tabs-bar case-file-tab-bar case-info-context-card flex w-full items-center gap-1 overflow-hidden px-1.5 py-1.5"
      >
        <div
          ref={tabsScrollRef}
          className="workspace-tabs-scroll flex min-w-0 flex-1 items-center overflow-x-auto no-scrollbar"
        >
          <div ref={tabsRowRef} className="flex items-center gap-1">
            {tabs.map(tab => {
              const isActive = isTabbableRoute && tab.id === activeTabId;
              const Icon = tab.type === 'case' ? Folder : tab.type === 'draft' ? FileText : tab.type === 'document' ? File : Search;

              return (
                <div
                  key={tab.id}
                  data-tab-item
                  onClick={() => handleTabClick(tab.id)}
                  className={[
                    'case-file-tab group flex w-[11rem] min-w-[8rem] max-w-[15rem] shrink-0 items-center gap-2 overflow-hidden rounded-lg px-3 py-2 transition-colors',
                    isActive ? 'workspace-tab-active' : 'workspace-tab-inactive',
                  ].join(' ')}
                >
                  <Icon
                    className={`workspace-tab-icon h-3.5 w-3.5 shrink-0 text-[#64748B] ${isActive ? 'opacity-80' : 'opacity-70'}`}
                  />
                  <span
                    className={`min-w-0 flex-1 truncate text-[12px] leading-none ${
                      isActive ? 'font-semibold' : 'font-medium'
                    }`}
                  >
                    {tab.title}
                  </span>
                  <button
                    type="button"
                    onClick={(e) => handleClose(e, tab.id)}
                    className={`workspace-tab-close shrink-0 rounded-full p-1 transition-colors ${
                      isActive
                        ? 'text-slate-500 hover:bg-slate-200/80'
                        : 'text-slate-500 opacity-0 hover:text-slate-700 group-hover:opacity-100 hover:bg-slate-200/60'
                    }`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              );
            })}
            {!pinNewTabButton && newTabButton}
          </div>
        </div>
        {pinNewTabButton && newTabButton}
      </div>
    </div>
  );
}
